import { useHttpClient } from '@wix/fe-essentials-standalone';
import { queryApp } from '@wix/ambassador-devcenter-v1-app/http';
import { getNamespaceSuggestionByName } from '@wix/ambassador-devcenter-apps-v1-app/http';
import { NamespaceType } from '@wix/ambassador-devcenter-apps-v1-app/types';

export const useBlocksService = () => {
  const httpClient = useHttpClient();

  const getNamespaceSuggestionsByType = async (
    name: string,
    type: NamespaceType,
  ) =>
    (
      await httpClient.request(getNamespaceSuggestionByName({ name, type }))
    ).data.namespace.split('/')[1];

  const getNamespaceSuggestions = async (name: string) => {
    const [account, company] = (
      await Promise.all([
        httpClient.request(
          getNamespaceSuggestionByName({ name, type: NamespaceType.ACCOUNT }),
        ),
        httpClient.request(
          getNamespaceSuggestionByName({ name, type: NamespaceType.COMPANY }),
        ),
      ])
    ).map((p) => p.data.namespace);
    const [accountPrefix, companyPrefix] = [account, company].map(
      (namespace) => namespace.split('/')[0],
    );

    if (accountPrefix !== companyPrefix) {
      return {
        account: accountPrefix,
        company: companyPrefix,
        namespace: company.split('/')[1],
      };
    }
    return { account: accountPrefix, namespace: account.split('/')[1] };
  };

  const getAllAppsFieldStartsWith = async (
    field: string,
    $startsWith: string,
  ) => {
    return (
      await httpClient.request(
        queryApp({
          query: {
            filter: {
              accountId: window.__USER_ID__,
              [field]: { $startsWith },
            },
            fields: [field],
          },
        }),
      )
    ).data.apps.map((app) => app[field]);
  };

  const isNamespaceExists = async (newNamespace: string) => {
    const allAppNamespaces = await getAllAppsFieldStartsWith(
      'namespace',
      newNamespace,
    );
    return allAppNamespaces.includes(newNamespace);
  };

  const getAppNameSuggestions = async (templateName: string) => {
    const allAppNames = await getAllAppsFieldStartsWith('name', templateName);

    let i = 1,
      newName = templateName;
    while (allAppNames.includes(newName)) {
      newName = `${templateName} ${i}`;
      i++;
    }

    const newNamespaces = await getNamespaceSuggestions(newName);

    return { newName, newNamespaces };
  };

  const isAppNameExists = async (name: string) =>
    (await getAllAppsFieldStartsWith('name', name)).some((app) => app === name);

  return {
    getAppNameSuggestions,
    getNamespaceSuggestionsByType,
    isNamespaceExists,
    isAppNameExists,
  };
};
